import { useMemo } from "react";

/**
 * Optimized hook to convert an array of IDs into options by looking up items in a list.
 * Uses a memoized Map for O(1) lookups instead of O(n) find operations.
 *
 * @param items - Array of items to create lookup map from
 * @param ids - Array of IDs to map to options
 * @returns Array of options with label and value properties
 *
 * @example
 * const languageOptions = useOptionsFromIds(bunchLanguageList, defaultTeam?.meta?.language);
 */
export const useOptionsFromIds = <T extends { id: any; name: string }>(
  items: T[] | undefined,
  ids: any[] | undefined
) => {
  return useMemo(() => {
    const map = new Map(items?.map((i) => [i.id, i]) || []);
    const result: Array<{ label: string; value: string }> = [];
    (ids || []).forEach((id) => {
      const item = map.get(id);
      if (item) {
        result.push({ label: item.name, value: item.name });
      }
    });
    return result;
  }, [items, ids]);
};
